// iter687 — Dashboard banner that nudges existing tenants to complete
// the Country Setup Wizard. Auto-hides when:
//   • Already done (branding.setup_complete === true), OR
//   • User dismissed for this session (sessionStorage flag), OR
//   • User isn't an admin.
//
// Click → opens the wizard. Stays out of the way otherwise.

import { useEffect, useState } from 'react';
import { Globe, X } from 'lucide-react';
import { useAuthStore } from '@/stores';
import api from '@/lib/api';
import { CountrySetupWizard } from '@/components/setup/CountrySetupWizard';

const DISMISS_KEY = 'iter687_country_setup_dismissed';

export default function DashboardSetupCountryBanner() {
  const user = useAuthStore?.(s => s.user);
  const [branding, setBranding] = useState(null);
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem(DISMISS_KEY) === '1') setDismissed(true);
    } catch { /* ignore */ }
  }, []);

  // Fetch current branding once — used to seed the wizard with
  // already-entered values + check setup_complete.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get('/pdf-exports/branding');
        if (alive) setBranding(data || {});
      } catch { if (alive) setBranding({}); }
    })();
    return () => { alive = false; };
  }, []);

  const isAdmin = (user?.role || '').toLowerCase().includes('admin');
  const done = branding && branding.setup_complete === true;
  const visible = !!branding && !done && !dismissed && isAdmin;

  if (!visible) return null;

  return (
    <>
      <div
        className="rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-sky-50 p-3 flex items-center justify-between gap-3"
        data-testid="dashboard-country-setup-banner"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-full bg-emerald-500/15 flex items-center justify-center shrink-0">
            <Globe size={18} className="text-emerald-700" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-stone-800">Finish setting up your country</div>
            <div className="text-[12px] text-stone-600 leading-snug">
              Pick your country and we&apos;ll auto-enable the right VAT rate, currency, ZATCA / Zakat, and compliance defaults — and hide what doesn&apos;t apply. Takes 30 seconds.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setOpen(true)}
            className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold"
            data-testid="dashboard-country-setup-start-btn"
          >
            Set up
          </button>
          <button
            onClick={() => {
              setDismissed(true);
              try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
            }}
            className="p-1.5 rounded-lg text-stone-500 hover:bg-white/60"
            title="Hide for this session"
            data-testid="dashboard-country-setup-dismiss-btn"
          >
            <X size={14} />
          </button>
        </div>
      </div>
      <CountrySetupWizard
        open={open}
        onOpenChange={setOpen}
        initialBranding={branding || {}}
        onDone={(updated) => {
          setBranding(updated);
          setOpen(false);
        }}
      />
    </>
  );
}
